import { formatMass, formatMoney } from '~/widgets/CostBomDemo/format'
import {
  COST_BOM_DEMO_STRINGS,
  COST_DRIVERS,
} from '~/widgets/CostBomDemo/strings'

const TOP_DRIVER_COUNT = 3

/** Above this, the largest component is better described by the gap it opens. */
const DOMINANCE_RATIO = 5
/** A component this large is the product's cost story on its own. */
const DOMINANT_SHARE = 0.5
/** A non-material bucket this close to the largest component is worth naming. */
const PARITY_TOLERANCE = 0.1
const PARITY_MIN_SHARE = 0.2

interface SummaryNode {
  name: string
  cost: number
  mass: number
  sources?: Array<{ n: string; p: number }>
  breakdown?: Record<string, number>
}

/** Root node per generated panel, mirroring the panel markup's own root row. */
function rootRow(panel: Element) {
  return panel.querySelector('.mi-row--parent[data-i]')
}

/**
 * The rows one level under the root.
 *
 * `MI_NODES` is flat, so the tree only exists in the panel markup: a child is
 * either a bare row or the header row of a nested group, both sitting directly
 * inside the root's `.mi-bom__children`.
 */
function firstLevelRows(panel: Element): Element[] {
  const children = panel.querySelector(
    '.mi-bom__rows > .mi-bom__group > .mi-bom__children',
  )
  if (!children) return []

  return Array.from(children.children)
    .map((child) =>
      child.classList.contains('mi-row')
        ? child
        : child.querySelector(':scope > .mi-row'),
    )
    .filter((row): row is Element => !!row && row.hasAttribute('data-i'))
}

/** A driver worth a fraction of a percent still contributes, so never show 0%. */
function percent(share: number) {
  if (share > 0 && share < 0.005) return '<1%'
  return `${Math.round(share * 100)}%`
}

/** Fills `{placeholder}` slots in a string template. */
function fill(template: string, values: Record<string, string>) {
  return template.replace(/\{(\w+)\}/g, (_, key) => values[key] ?? '')
}

function el(tag: string, className: string, text?: string) {
  const node = document.createElement(tag)
  node.className = className
  if (text !== undefined) node.textContent = text
  return node
}

const CHEVRON =
  '<svg viewBox="0 0 10 10" fill="none" aria-hidden="true"><path d="M2.5 4L5 6.5L7.5 4" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>'

/** Below this the band stacks, and each pane becomes collapsible. */
const STACKED = window.matchMedia('(max-width: 900px)')

/**
 * Where isolating a driver by pointer makes sense. On a phone the segments are
 * 14px wide at their smallest and the rows below already name every share, so
 * there is nothing to reveal and nothing big enough to hit.
 */
const CAN_ISOLATE = window.matchMedia('(hover: hover) and (min-width: 601px)')

/**
 * A pane: its header and an empty body for the caller to fill.
 *
 * The header is a button so the stacked layout can collapse the pane; above the
 * breakpoint it renders as the plain label it looks like and the click handler
 * declines, because three panes side by side have nothing to gain from folding.
 *
 */
function pane(label: string, open = true) {
  const box = el('div', 'mi-summary__pane')
  const body = el('div', 'mi-summary__body')

  const toggle = document.createElement('button')
  toggle.type = 'button'
  toggle.className = 'mi-summary__toggle'
  toggle.appendChild(el('span', 'mi-summary__label', label))
  const chevron = el('span', 'mi-summary__toggle-chevron')
  chevron.innerHTML = CHEVRON
  toggle.appendChild(chevron)

  box.appendChild(toggle)
  box.appendChild(body)
  box.dataset.open = String(open)
  toggle.setAttribute('aria-expanded', String(open))

  toggle.addEventListener('click', () => {
    if (!STACKED.matches) return
    const next = box.dataset.open !== 'true'
    box.dataset.open = String(next)
    toggle.setAttribute('aria-expanded', String(next))
  })

  return { box, body }
}

/** A labelled figure under the should cost: total mass, sourcing country. */
function metaRow(label: string, value: string | HTMLElement) {
  const row = el('div', 'mi-summary__meta')
  if (typeof value !== 'string') row.classList.add('mi-summary__meta--tag')
  row.appendChild(el('span', 'mi-summary__meta-label', label))
  if (typeof value === 'string') {
    row.appendChild(el('span', 'mi-summary__meta-val', value))
  } else {
    value.classList.add('mi-summary__meta-val')
    row.appendChild(value)
  }
  return row
}

/**
 * Sourcing tag.
 *
 * Static text, not a control. Each sample carries one model priced in one
 * country, so there is nothing to switch the tag to.
 */
function countryTag(country: string) {
  const tag = el('span', 'mi-summary__tag')
  tag.appendChild(el('span', 'mi-summary__tag-val', country))
  return tag
}

function dominantSource(node: SummaryNode) {
  if (!node.sources?.length) return null
  return node.sources.reduce((a, b) => ((b.p || 0) > (a.p || 0) ? b : a)).n
}

function shouldCostPane(node: SummaryNode, panelKey: string) {
  const { box, body } = pane(COST_BOM_DEMO_STRINGS.estimatedShouldCost)

  const cost = el('div', 'mi-summary__cost')
  cost.appendChild(el('span', 'mi-summary__cost-num', formatMoney(node.cost)))
  cost.appendChild(
    el('span', 'mi-summary__cost-unit', COST_BOM_DEMO_STRINGS.currency),
  )
  body.appendChild(cost)

  body.appendChild(
    metaRow(COST_BOM_DEMO_STRINGS.totalMass, formatMass(node.mass, panelKey)),
  )

  // Sourcing belongs beside the cost it produced rather than on the tab: the
  // figure only means anything once you know which country it was priced in.
  const country = dominantSource(node)
  if (country) {
    body.appendChild(
      metaRow(COST_BOM_DEMO_STRINGS.sourcedFrom, countryTag(country)),
    )
  }

  return box
}

function compositionPane(node: SummaryNode) {
  // Closed by default. The rule that acts on this lives inside the stacking
  // breakpoint, so the pane is open as ever on a wide screen and folded on a
  // narrow one, where three open panes leave no room for the table below.
  const { box, body } = pane(COST_BOM_DEMO_STRINGS.costComposition, false)
  const bar = el('div', 'mi-summary__bar')
  const rows = el('div', 'mi-summary__rows')
  const pairs: Array<{ modifier: string; seg: HTMLElement; row: HTMLElement }> =
    []

  const total = COST_DRIVERS.reduce(
    (sum, { key }) => sum + (node.breakdown?.[key] || 0),
    0,
  )

  COST_DRIVERS.forEach(({ key, modifier }) => {
    const value = node.breakdown?.[key] || 0
    if (!value) return

    // The bar carries the proportions the rows only state as numbers, so a
    // dominant driver is visible before anything is read.
    const seg = el('span', `mi-summary__seg mi-summary__seg--${modifier}`)
    seg.style.flexGrow = String(total ? value / total : 0)
    bar.appendChild(seg)

    const row = el('div', 'mi-summary__row')
    row.appendChild(el('i', `mi-lg mi-lg--${modifier}`))
    row.appendChild(el('span', 'mi-summary__row-name', key))
    row.appendChild(
      el('span', 'mi-summary__row-pct', percent(total ? value / total : 0)),
    )
    row.appendChild(el('span', 'mi-summary__row-val', formatMoney(value)))
    rows.appendChild(row)

    pairs.push({ modifier, seg, row })
  })

  function clear() {
    delete box.dataset.focus
    pairs.forEach(({ seg, row }) => {
      seg.classList.remove('is-focus')
      row.classList.remove('is-focus')
    })
  }

  // A segment and its row are the same driver seen twice, so hovering either one
  // isolates the pair and lets the other three fall back to grey. Focusing
  // always clears first, so moving straight from one driver to the next cannot
  // leave two highlighted.
  pairs.forEach(({ modifier, seg, row }) => {
    const focus = () => {
      if (!CAN_ISOLATE.matches) return
      clear()
      box.dataset.focus = modifier
      seg.classList.add('is-focus')
      row.classList.add('is-focus')
    }
    seg.addEventListener('mouseenter', focus)
    row.addEventListener('mouseenter', focus)
  })
  box.addEventListener('mouseleave', clear)

  body.appendChild(bar)
  body.appendChild(rows)
  return box
}

/** Trailing parenthetical repeats the name in prose ("Silicon die (silicon)"). */
function plainName(name: string) {
  return name.replace(/\s*\([^()]*\)\s*$/, '')
}

/**
 * Picks what is worth saying about THIS model rather than applying one template
 * to every product. Each rule states something that is only true under its own
 * condition, so a die that dwarfs its board, a ferment whose plant overhead
 * rivals its sugar, and a cell whose cost is spread over three layers each get
 * their own sentence.
 */
function driverInsights(
  node0: SummaryNode,
  all: Array<{ node: { name: string; cost: number } }>,
) {
  const rootCost = node0.cost
  const lead = all[0].node
  const second = all[1]?.node
  const ratio = second?.cost ? lead.cost / second.cost : 0
  const leadShare = rootCost ? lead.cost / rootCost : 0
  const materials = node0.breakdown?.Materials || 0

  const nonMaterial = Object.entries(node0.breakdown || {})
    .filter(([key]) => key !== 'Materials')
    .sort((a, b) => b[1] - a[1])[0]

  const top = all.slice(0, TOP_DRIVER_COUNT)
  const topShare = rootCost
    ? top.reduce((sum, e) => sum + e.node.cost, 0) / rootCost
    : 0

  let headline: string
  if (leadShare >= DOMINANT_SHARE) {
    headline = fill(COST_BOM_DEMO_STRINGS.dominanceHeadline, {
      name: plainName(lead.name),
      share: percent(leadShare),
    })
  } else if (
    nonMaterial &&
    nonMaterial[1] / rootCost >= PARITY_MIN_SHARE &&
    Math.abs(nonMaterial[1] - lead.cost) / lead.cost <= PARITY_TOLERANCE
  ) {
    headline = fill(COST_BOM_DEMO_STRINGS.processParityHeadline, {
      driver: nonMaterial[0],
    })
  } else {
    headline = fill(COST_BOM_DEMO_STRINGS.concentrationHeadline, {
      n: String(top.length),
      total: String(all.length),
      share: percent(topShare),
    })
  }

  let closing = ''
  if (ratio >= DOMINANCE_RATIO) {
    closing = fill(COST_BOM_DEMO_STRINGS.dominanceClosing, {
      name: plainName(lead.name),
      times: `${ratio.toFixed(ratio >= 10 ? 0 : 1)}×`,
    })
  } else if (materials > 0) {
    closing = fill(COST_BOM_DEMO_STRINGS.materialClosing, {
      materialShare: percent(lead.cost / materials),
      materials: formatMoney(materials),
    })
  }

  return { headline, closing }
}

function driversPane(panel: Element, node0: SummaryNode) {
  const nodes = window.MI_NODES || {}
  const rootCost = node0.cost
  const all = firstLevelRows(panel)
    .map((row) => ({ node: nodes[row.getAttribute('data-i') || ''] }))
    .filter((entry) => !!entry.node)
    .sort((a, b) => b.node.cost - a.node.cost)
  const ranked = all.slice(0, TOP_DRIVER_COUNT)

  if (!ranked.length) return null

  // Folded on a narrow screen for the same reason as the composition pane.
  const { box, body } = pane(COST_BOM_DEMO_STRINGS.whereCostSits, false)

  const insights = driverInsights(node0, all)
  body.appendChild(el('div', 'mi-summary__insight', insights.headline))

  const list = el('div', 'mi-summary__drivers')

  ranked.forEach(({ node }, index) => {
    const share = rootCost ? node.cost / rootCost : 0

    const entry = el('div', 'mi-summary__driver')

    entry.appendChild(el('span', 'mi-summary__driver-rank', String(index + 1)))

    // Names run long enough to need truncating, so the full one stays reachable
    // on hover.
    const name = el('span', 'mi-summary__driver-name', node.name)
    name.title = node.name
    entry.appendChild(name)

    // Share first, then the amount, matching the composition rows above: the
    // two panes answer the same shape of question and should read the same way.
    entry.appendChild(el('span', 'mi-summary__driver-pct', percent(share)))
    entry.appendChild(
      el('span', 'mi-summary__driver-val', formatMoney(node.cost)),
    )

    list.appendChild(entry)
  })

  body.appendChild(list)

  if (insights.closing) {
    body.appendChild(el('div', 'mi-summary__closing', insights.closing))
  }

  return box
}

/**
 * Placeholder proportions for the idle band, mirroring the shares the seeded
 * bars in the idle table already use. They are shapes, not claims: no product is
 * chosen yet, so every figure beside them is a skeleton.
 */
const IDLE_SHARES = [0.6, 0.15, 0.1, 0.15]

function skeleton(modifier: string, width?: string) {
  const block = el('span', `mi-skel-block mi-skel-block--${modifier}`)
  if (width) block.style.setProperty('--skel-w', width)
  return block
}

/**
 * The idle band: the result layout with its figures held as skeletons, so the
 * page does not change shape when a model is generated.
 */
function idleBand() {
  const band = el('div', 'mi-summary mi-summary--idle')

  const cost = pane(COST_BOM_DEMO_STRINGS.estimatedShouldCost, true)
  const figure = el('div', 'mi-summary__cost')
  figure.appendChild(skeleton('cost'))
  cost.body.appendChild(figure)
  cost.body.appendChild(
    metaRow(COST_BOM_DEMO_STRINGS.totalMass, skeleton('val')),
  )
  band.appendChild(cost.box)

  // Names and colours are fixed, so they read as real while the figures wait.
  const comp = pane(COST_BOM_DEMO_STRINGS.costComposition, false)
  const bar = el('div', 'mi-summary__bar')
  const rows = el('div', 'mi-summary__rows')
  COST_DRIVERS.forEach(({ key, modifier }, index) => {
    const seg = el('span', `mi-summary__seg mi-summary__seg--${modifier}`)
    seg.style.flexGrow = String(IDLE_SHARES[index] || 0)
    bar.appendChild(seg)

    const row = el('div', 'mi-summary__row')
    row.appendChild(el('i', `mi-lg mi-lg--${modifier}`))
    row.appendChild(el('span', 'mi-summary__row-name', key))
    row.appendChild(skeleton('pct'))
    row.appendChild(skeleton('val'))
    rows.appendChild(row)
  })
  comp.body.appendChild(bar)
  comp.body.appendChild(rows)
  band.appendChild(comp.box)

  const drivers = pane(COST_BOM_DEMO_STRINGS.whereCostSits, false)
  // The insight line has nothing to claim yet, so the pane holds its place with
  // a skeleton instead of a sentence about a product nobody picked.
  drivers.body.appendChild(skeleton('insight'))
  const list = el('div', 'mi-summary__drivers')
  // No share bar at all while idle: no product is chosen, so there is no
  // proportion to draw and an empty rail is just a line.
  Array.from({ length: TOP_DRIVER_COUNT }).forEach((_, index) => {
    const entry = el('div', 'mi-summary__driver mi-summary__driver--idle')
    entry.appendChild(el('span', 'mi-summary__driver-rank', String(index + 1)))
    entry.appendChild(skeleton('name', `${62 - index * 8}%`))
    entry.appendChild(skeleton('val'))
    list.appendChild(entry)
  })
  drivers.body.appendChild(list)
  band.appendChild(drivers.box)

  return band
}

/**
 * Builds the idle band above the placeholder table, so the default page carries
 * the same three panes a generated model does.
 */
export function mountCostSummaryIdle(root: ParentNode) {
  const panel = root.querySelector('.mi-tabpanel[data-panel="idle"]')
  const table = panel?.querySelector('.mi-bom')
  if (!table || panel?.querySelector('.mi-summary')) return

  table.insertAdjacentElement('beforebegin', idleBand())
}

/**
 * Builds the summary card that sits above each generated BoM table: the
 * product's should cost, how that cost is composed, and the components carrying
 * most of it. Every figure is derived from `MI_NODES`, so the card cannot drift
 * from the table underneath it.
 */
export function mountCostSummary(root: ParentNode) {
  const nodes = window.MI_NODES || {}

  root.querySelectorAll('.mi-tabpanel[data-panel]').forEach((panel) => {
    const panelKey = panel.getAttribute('data-panel') || ''
    if (panelKey === 'idle') return

    const table = panel.querySelector('.mi-bom')
    if (!table || panel.querySelector('.mi-summary')) return

    const rootEl = rootRow(panel)
    const node = rootEl && nodes[rootEl.getAttribute('data-i') || '']
    if (!node) return

    // A generated model opens all three panes: the visitor asked for this, so
    // the findings are shown and collapsing is theirs to do.
    const card = el('div', 'mi-summary')
    card.appendChild(shouldCostPane(node, panelKey))
    card.appendChild(compositionPane(node))

    const drivers = driversPane(panel, node)
    if (drivers) card.appendChild(drivers)

    table.insertAdjacentElement('beforebegin', card)
  })
}
